/**
 * Pure math/geometry helpers (verbatim port of debugbase MathHelper).
 *
 * Dropped from the class version: the stateful `_distanceX`/`_distanceY`
 * fields and their getters (`distanceX` had an empty body and returned
 * undefined; neither getter had any call sites), and an unused `_geo`
 * DGeometry instance. `distance()` is now fully pure.
 */

export interface Size {
	width: number;
	height: number;
}

export interface Point {
	x: number;
	y: number;
}

//https://stackoverflow.com/questions/42110701/how-to-calculate-percentage-between-the-range-of-two-values-a-third-value-is-in
export function mapBetween(currentNum: number, minAllowed: number, maxAllowed: number, min: number, max: number): number {
	return ((maxAllowed - minAllowed) * (currentNum - min)) / (max - min) + minAllowed;
}

export function getInbetween(value: number, bottom_value: number, top_value: number): number {
	const range = top_value - bottom_value;
	const percent = (value - bottom_value) / range;
	return percent;
}

export function constrain(value: number, min: number, max: number): number {
	if (value > max) {
		value = max;
	} else if (value < min) {
		value = min;
	}
	return value;
}

//Get a ratio value between two values by passing a ratio
//i.e between -1.2 and 2
///getRangeRatio(0.4, -1.2, 2);
export function getRangeRatio(ratio: number, lowerValue: number, topValue: number): number {
	const range: number = topValue - lowerValue;
	return lowerValue + ratio * range;
}

//Get the scale an item should be to fit into a space:
export function getBestFitScale(imageDimensions: Size, containerDimensions: Size): number {
	let scale = 1.0;
	const aspectRatio = imageDimensions.width / imageDimensions.height;
	const targetRatio = containerDimensions.width / containerDimensions.height;

	if (targetRatio > aspectRatio) {
		scale = containerDimensions.width / imageDimensions.width;
	} else {
		scale = containerDimensions.height / imageDimensions.height;
	}

	return scale;
}

/** Emulate CSS cover logic */
export function getCoverSize(imageDimensions: Size, containerDimensions: Size): any {
	const originalRatios = {
		width: containerDimensions.width / imageDimensions.width,
		height: containerDimensions.height / imageDimensions.height
	};

	// formula for cover:
	const coverRatio = Math.max(originalRatios.width, originalRatios.height);

	// result:
	return {
		width: imageDimensions.width * coverRatio,
		height: imageDimensions.height * coverRatio
	};
}

export function distance(point1: Point, point2: Point): number {
	const distanceX = point1.x - point2.x;
	const distanceY = point1.y - point2.y;
	return Math.sqrt(distanceX * distanceX + distanceY * distanceY);
}

/**
 * Seeded random helper. Quirks preserved from the original: `miniusSeed`
 * (sic) flips only the seed portion's sign half the time; `miniusAll` flips
 * the whole result half the time; `round` rounds the final value.
 */
export function random(
	base: number = 0,
	seed: number = 1.0,
	miniusSeed: boolean = false,
	round: boolean = false,
	miniusAll: boolean = false
): number {
	const d = miniusSeed && Math.random() < 0.5 ? -1 : 1;
	let num = base + Math.random() * (seed * d);

	if (miniusAll) {
		num *= Math.random() < 0.5 ? -1 : 1;
	}
	return round ? Math.round(num) : num;
}
